import React, { memo, useEffect, useState, useCallback, useRef } from 'react';
import { Platform, View, Text, TextInput, Pressable, ScrollView, Image, Alert, ActivityIndicator } from 'react-native';
import { HIDE_ADD_COMPANY_MODAL, SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, XStack, YStack, SizableText } from 'tamagui';
import { AntDesign, Feather, MaterialIcons, FontAwesome } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import apis from '@api/api';
import getId from '@api/getId';
import { useQuery } from '@tanstack/react-query';
import * as ImagePicker from 'expo-image-picker';
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';
import addCompanyInfo from '../../../navigation/screens/TopLevel/Components/AddCompanyInfo';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import * as Animatable from 'react-native-animatable';

interface CompanyFormData {
  company_name: string;
  company_number?: string;
  industry_type?: string;
  role?: string;
  interests?: string;
  details?: string;
  country?: string;
  logo?: string;
}

interface BillingFormData {
  useExistingBilling: boolean;
  billingEmail: string;
  billingPhone?: string;
  streetAddress1: string;
  streetAddress2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  taxId?: string;
  businessRegistrationNumber?: string;
  paymentMethodId?: string;
}

const AddCompanyModal = () => {
  const addCompanyVisible = useSelector((state: DefaultRootState) => state.modals.formModal.modalDisplayed);
  const dispatch = useDispatch();
  const theme = themeSelector();
  const userId = getId();
  const [currentStep, setCurrentStep] = useState(1);
  const [companyData, setCompanyData] = useState<CompanyFormData>({
    company_name: '',
    company_number: '',
    industry_type: '',
    role: '',
    interests: '',
    details: '',
    country: '',
    logo: '',
  });
  const [billingData, setBillingData] = useState<BillingFormData>({
    useExistingBilling: false,
    billingEmail: '',
    billingPhone: '',
    streetAddress1: '',
    streetAddress2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    taxId: '',
    businessRegistrationNumber: '',
  });
  const [image, setImage] = useState('null');
  const [imageData, setImageData] = useState({});
  const [hovered, setHovered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const formRef = useRef<any>(null);
  const formValuesRef = useRef<CompanyFormData>(companyData);

  // Check for existing billing profile
  const { data: existingBilling, isLoading: billingLoading } = useQuery({
    queryKey: ['userBillingInfo', userId],
    queryFn: async () => {
      if (!userId) return null;
      try {
        const res = await apis.retrieveUserBillingInfo(userId);
        return res.data?.data || null;
      } catch (error) {
        console.error('Error fetching billing info:', error);
        return null;
      }
    },
    enabled: !!userId && addCompanyVisible,
    retry: 0,
  });

  // Check if user has active billing profile
  const hasExistingBilling = existingBilling && Object.values(existingBilling).some(
    (billing: any) => billing?.isActive && billing?.billingId
  );

  // Get user email from Redux state for pre-filling
  const userData = useSelector((state: DefaultRootState) => state.promiseData.data?.userData);
  const userEmail = userData?.email || '';

  useEffect(() => {
    if (hasExistingBilling && currentStep === 2 && !billingData.billingEmail) {
      // Pre-fill with user email if using existing billing
      setBillingData(prev => ({
        ...prev,
        useExistingBilling: true,
        billingEmail: userEmail,
      }));
    }
  }, [hasExistingBilling, currentStep, userEmail]);

  const handleClose = useCallback(() => {
    setCurrentStep(1);
    setCompanyData({
      company_name: '',
      company_number: '',
      industry_type: '',
      role: '',
      interests: '',
      details: '',
      country: '',
      logo: '',
    });
    setBillingData({
      useExistingBilling: false,
      billingEmail: '',
      billingPhone: '',
      streetAddress1: '',
      streetAddress2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      taxId: '',
      businessRegistrationNumber: '',
    });
    setImage('null');
    setImageData({});
    setErrors({});
    dispatch({ type: HIDE_ADD_COMPANY_MODAL });
  }, [dispatch]);

  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!companyData.company_name?.trim()) {
      newErrors.company_name = 'Company name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Billing email is always required
    if (!billingData.billingEmail?.trim()) {
      newErrors.billingEmail = 'Billing email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingData.billingEmail)) {
      newErrors.billingEmail = 'Invalid email format';
    }
    
    // Address fields only required if not using existing billing
    if (!billingData.useExistingBilling) {
      if (!billingData.streetAddress1?.trim()) {
        newErrors.streetAddress1 = 'Street address is required';
      }
      if (!billingData.city?.trim()) {
        newErrors.city = 'City is required';
      }
      if (!billingData.state?.trim()) {
        newErrors.state = 'State/Province is required';
      }
      if (!billingData.postalCode?.trim()) {
        newErrors.postalCode = 'Postal code is required';
      }
      if (!billingData.country?.trim()) {
        newErrors.country = 'Country is required';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      // Trigger form validation and capture values
      if (formRef.current?.submitForm) {
        try {
          await formRef.current.submitForm();
          // Values will be captured in submitFunction
          // Merge any additional values
          setCompanyData(prev => ({ ...prev, ...formValuesRef.current }));
        } catch (error) {
          // Form validation failed
          console.error('Form validation error:', error);
        }
      }
      // Merge any form values that might have been captured
      setCompanyData(prev => ({ ...prev, ...formValuesRef.current }));
      if (validateStep1()) {
        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      if (validateStep2()) {
        setCurrentStep(3);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const _resizeImage = async () => {
    const pickImage = async () => {
      let result: ImagePicker.ImagePickerResult;
      const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();

      if (permissionResult.granted === false) {
        Alert.alert("Permission Required", "You've refused to allow this app to access your photos!");
        return;
      }

      result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        base64: true,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 1,
      });

      if (!result.canceled && result.assets?.[0]) {
        const uri = result.assets[0].uri;
        setImage(uri);
        const manipResultImg = await manipulateAsync(uri, [
          { resize: { height: 200 } }
        ], { format: SaveFormat.PNG, compress: 1, base64: true });
        
        let img = manipResultImg.base64;
        setImageData({ img });
        setCompanyData(prev => ({ ...prev, logo: img }));
      }
    };

    pickImage();
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Always create a new billingId for the company, even if user has existing billing
      // The backend will create a new Stripe customer for the company
      const payload = {
        ...companyData,
        organizationName: companyData.company_name,
        organizationNumber: companyData.company_number,
        email: billingData.billingEmail || undefined,
        phone: billingData.billingPhone || undefined,
        billingEmail: billingData.billingEmail || undefined,
        billingPhone: billingData.billingPhone || undefined,
        // Always send billing information to create new company billing account
        // Even if useExistingBilling is true, we still create a new customer for the company
        billing: {
          // Note: useExistingBilling flag is informational only
          // Backend will always create a new Stripe customer for the company
          useExisting: billingData.useExistingBilling,
          address: billingData.useExistingBilling ? undefined : {
            line1: billingData.streetAddress1,
            line2: billingData.streetAddress2,
            streetAddress1: billingData.streetAddress1,
            streetAddress2: billingData.streetAddress2,
            city: billingData.city,
            state: billingData.state,
            postal_code: billingData.postalCode,
            postalCode: billingData.postalCode,
            country: billingData.country,
          },
          taxId: billingData.taxId,
          businessRegistrationNumber: billingData.businessRegistrationNumber,
        },
      };

      await apis.addCompany(payload);
      
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: "Company pending approval, you will receive further instructions in your email",
          type: "success"
        }
      });
      
      handleClose();
    } catch (error: any) {
      console.error('Error creating company:', error);
      Alert.alert(
        'Error',
        error.response?.data?.error || error.message || 'Failed to create company. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => {
    const steps = ['Company Details', 'Billing Details', 'Review & Submit'];
    return (
      <XStack gap="$2" justifyContent="center" marginVertical="$4">
        {steps.map((step, index) => {
          const stepNum = index + 1;
          const isActive = stepNum === currentStep;
          const isCompleted = stepNum < currentStep;
          
          return (
            <XStack key={stepNum} alignItems="center" gap="$2">
              <View
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  backgroundColor: isActive
                    ? theme ? '#3b82f6' : '#2563eb'
                    : isCompleted
                    ? theme ? '#10b981' : '#059669'
                    : theme ? '#e5e7eb' : '#4b5563',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isCompleted ? (
                  <AntDesign name="check" size={16} color="white" />
                ) : (
                  <Text style={{
                    color: isActive ? 'white' : theme ? '#6b7280' : '#9ca3af',
                    fontWeight: '600',
                    fontSize: 14,
                  }}>
                    {stepNum}
                  </Text>
                )}
              </View>
              {index < steps.length - 1 && (
                <View
                  style={{
                    width: 40,
                    height: 2,
                    backgroundColor: isCompleted
                      ? theme ? '#10b981' : '#059669'
                      : theme ? '#e5e7eb' : '#4b5563',
                  }}
                />
              )}
            </XStack>
          );
        })}
      </XStack>
    );
  };

  const renderStep1 = () => (
    <ScrollView style={{ maxHeight: 500 }}>
      <YStack gap="$4">
        {/* Logo Upload */}
        <YStack gap="$2">
          <SizableText
            style={{
              color: theme ? '#374151' : '#f9fafb',
              fontWeight: '600',
              fontSize: 14,
            }}
          >
            Company Logo
          </SizableText>
          <Pressable
            onPress={_resizeImage}
            onHoverIn={() => setHovered(true)}
            onHoverOut={() => setHovered(false)}
          >
            <View
              style={{
                minHeight: 200,
                borderRadius: 20,
                borderWidth: image !== 'null' && imageData ? 0 : 3,
                justifyContent: 'center',
                padding: 20,
                borderStyle: 'dashed',
                alignContent: 'center',
                alignSelf: 'stretch',
                alignItems: 'center',
                backgroundColor: hovered
                  ? theme ? '#f3f4f6' : '#374151'
                  : theme ? '#ffffff' : '#1f2937',
                borderColor: theme ? '#d1d5db' : '#6b7280',
              }}
            >
              {image !== 'null' && imageData ? (
                <Animatable.View useNativeDriver animation="bounceIn" style={{ flex: 1 }}>
                  <Pressable
                    style={{ position: 'absolute', top: -50, right: 0, zIndex: 1 }}
                    onPress={() => {
                      setImage('null');
                      setImageData({});
                      setCompanyData(prev => ({ ...prev, logo: '' }));
                    }}
                  >
                    <FontAwesome name="times" size={24} color="red" />
                  </Pressable>
                  <Image
                    source={{ uri: image }}
                    style={{
                      top: -50,
                      flex: 1,
                      resizeMode: "center",
                      width: 300,
                      minHeight: 120,
                    }}
                  />
                </Animatable.View>
              ) : (
                <YStack alignItems="center" gap="$2">
                  <AntDesign name="plus" size={32} color={theme ? '#6b7280' : '#9ca3af'} />
                  <Text style={{
                    color: theme ? '#6b7280' : '#9ca3af',
                    fontSize: 14,
                  }}>
                    Add Logo
                  </Text>
                </YStack>
              )}
            </View>
          </Pressable>
        </YStack>

        {/* Company Form Fields */}
        <GenerateSchemaForm
          scrollEnabled={false}
          schema={addCompanyInfo}
          customStyling={{ flex: 1 }}
          nullify={['details']}
          hideButton={true}
          initialData={companyData}
          submitFunction={async (values) => {
            // Capture form values when form is interacted with
            formValuesRef.current = { ...formValuesRef.current, ...values };
            setCompanyData(prev => ({ ...prev, ...values }));
            return values;
          }}
          ref={formRef}
          fieldStyle={{
            backgroundColor: theme ? '#ffffff' : '#1f2937',
            borderColor: theme ? '#e5e7eb' : '#4b5563',
            borderRadius: 12,
            padding: 16,
            marginBottom: 16,
          }}
          labelStyle={{
            color: theme ? '#374151' : '#f9fafb',
            fontWeight: '600',
            fontSize: 14,
            marginBottom: 8,
          }}
        />
      </YStack>
    </ScrollView>
  );

  const renderStep2 = () => {
    if (billingLoading) {
      return (
        <View style={{ padding: 40, alignItems: 'center' }}>
          <ActivityIndicator size="large" color={theme ? '#3b82f6' : '#2563eb'} />
          <Text style={{
            marginTop: 16,
            color: theme ? '#6b7280' : '#9ca3af',
          }}>
            Checking billing information...
          </Text>
        </View>
      );
    }

    return (
      <ScrollView style={{ maxHeight: 500 }}>
        <YStack gap="$4">
          {/* Existing Billing Option */}
          {hasExistingBilling && (
            <View
              style={{
                backgroundColor: theme ? '#f0f9ff' : '#0c4a6e',
                borderRadius: 16,
                padding: 20,
                borderWidth: 1,
                borderColor: theme ? '#bae6fd' : '#075985',
              }}
            >
              <XStack alignItems="center" justifyContent="space-between">
                <XStack alignItems="center" gap="$3" flex={1}>
                  <MaterialIcons
                    name="account-circle"
                    size={24}
                    color={theme ? "#0ea5e9" : "#38bdf8"}
                  />
                  <YStack flex={1}>
                    <Text style={{
                      fontSize: 16,
                      fontWeight: '600',
                      color: theme ? '#1e293b' : '#f1f5f9',
                      marginBottom: 4,
                    }}>
                      Use Account Email
                    </Text>
                    <Text style={{
                      fontSize: 12,
                      color: theme ? '#64748b' : '#94a3b8',
                    }}>
                      A new billing account will be created for this company using your account email
                    </Text>
                  </YStack>
                </XStack>
                <Pressable
                  onPress={() => {
                    setBillingData(prev => ({
                      ...prev,
                      useExistingBilling: !prev.useExistingBilling,
                    }));
                  }}
                  style={{
                    padding: 8,
                    borderRadius: 8,
                    backgroundColor: billingData.useExistingBilling
                      ? theme ? '#3b82f6' : '#2563eb'
                      : theme ? '#ffffff' : '#1f2937',
                    borderWidth: 1,
                    borderColor: theme ? '#e5e7eb' : '#4b5563',
                  }}
                >
                  {billingData.useExistingBilling && (
                    <AntDesign name="check" size={16} color="white" />
                  )}
                </Pressable>
              </XStack>
            </View>
          )}

          {/* Billing Email - Always required, even if using existing billing */}
          <YStack gap="$2">
            <SizableText
              style={{
                color: theme ? '#374151' : '#f9fafb',
                fontWeight: '600',
                fontSize: 14,
              }}
            >
              Billing Email *
            </SizableText>
            <TextInput
              value={billingData.billingEmail}
              onChangeText={(text) =>
                setBillingData(prev => ({ ...prev, billingEmail: text }))
              }
              placeholder="billing@company.com"
              placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
              editable={!billingData.useExistingBilling}
              style={{
                fontSize: 16,
                padding: 12,
                borderWidth: 1,
                borderColor: errors.billingEmail
                  ? '#ef4444'
                  : theme ? '#e5e7eb' : '#4b5563',
                borderRadius: 12,
                backgroundColor: billingData.useExistingBilling
                  ? theme ? '#f3f4f6' : '#374151'
                  : theme ? '#f8fafc' : '#374151',
                color: theme ? '#1f2937' : '#f9fafb',
                minHeight: 48,
                opacity: billingData.useExistingBilling ? 0.7 : 1,
              }}
            />
            {errors.billingEmail && (
              <Text style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>
                {errors.billingEmail}
              </Text>
            )}
            {billingData.useExistingBilling && (
              <Text style={{
                color: theme ? '#6b7280' : '#9ca3af',
                fontSize: 12,
                fontStyle: 'italic',
              }}>
                Using your account email. A new billing account will be created for this company.
              </Text>
            )}
          </YStack>

          {!billingData.useExistingBilling && (
            <>

              {/* Billing Phone */}
              <YStack gap="$2">
                <SizableText
                  style={{
                    color: theme ? '#374151' : '#f9fafb',
                    fontWeight: '600',
                    fontSize: 14,
                  }}
                >
                  Billing Phone
                </SizableText>
                <TextInput
                  value={billingData.billingPhone}
                  onChangeText={(text) =>
                    setBillingData(prev => ({ ...prev, billingPhone: text }))
                  }
                  placeholder="+1 (555) 123-4567"
                  placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                  style={{
                    fontSize: 16,
                    padding: 12,
                    borderWidth: 1,
                    borderColor: theme ? '#e5e7eb' : '#4b5563',
                    borderRadius: 12,
                    backgroundColor: theme ? '#f8fafc' : '#374151',
                    color: theme ? '#1f2937' : '#f9fafb',
                    minHeight: 48,
                  }}
                />
              </YStack>

              {/* Billing Address */}
              <YStack gap="$2">
                <SizableText
                  style={{
                    color: theme ? '#374151' : '#f9fafb',
                    fontWeight: '600',
                    fontSize: 14,
                  }}
                >
                  Street Address *
                </SizableText>
                <TextInput
                  value={billingData.streetAddress1}
                  onChangeText={(text) =>
                    setBillingData(prev => ({ ...prev, streetAddress1: text }))
                  }
                  placeholder="123 Main Street"
                  placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                  style={{
                    fontSize: 16,
                    padding: 12,
                    borderWidth: 1,
                    borderColor: errors.streetAddress1
                      ? '#ef4444'
                      : theme ? '#e5e7eb' : '#4b5563',
                    borderRadius: 12,
                    backgroundColor: theme ? '#f8fafc' : '#374151',
                    color: theme ? '#1f2937' : '#f9fafb',
                    minHeight: 48,
                  }}
                />
                {errors.streetAddress1 && (
                  <Text style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>
                    {errors.streetAddress1}
                  </Text>
                )}
              </YStack>

              <TextInput
                value={billingData.streetAddress2}
                onChangeText={(text) =>
                  setBillingData(prev => ({ ...prev, streetAddress2: text }))
                }
                placeholder="Apartment, suite, etc. (optional)"
                placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                style={{
                  fontSize: 16,
                  padding: 12,
                  borderWidth: 1,
                  borderColor: theme ? '#e5e7eb' : '#4b5563',
                  borderRadius: 12,
                  backgroundColor: theme ? '#f8fafc' : '#374151',
                  color: theme ? '#1f2937' : '#f9fafb',
                  minHeight: 48,
                  marginTop: 8,
                }}
              />

              <XStack gap="$3">
                <YStack gap="$2" flex={1}>
                  <SizableText
                    style={{
                      color: theme ? '#374151' : '#f9fafb',
                      fontWeight: '600',
                      fontSize: 14,
                    }}
                  >
                    City *
                  </SizableText>
                  <TextInput
                    value={billingData.city}
                    onChangeText={(text) =>
                      setBillingData(prev => ({ ...prev, city: text }))
                    }
                    placeholder="City"
                    placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                    style={{
                      fontSize: 16,
                      padding: 12,
                      borderWidth: 1,
                      borderColor: errors.city
                        ? '#ef4444'
                        : theme ? '#e5e7eb' : '#4b5563',
                      borderRadius: 12,
                      backgroundColor: theme ? '#f8fafc' : '#374151',
                      color: theme ? '#1f2937' : '#f9fafb',
                      minHeight: 48,
                    }}
                  />
                  {errors.city && (
                    <Text style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>
                      {errors.city}
                    </Text>
                  )}
                </YStack>

                <YStack gap="$2" flex={1}>
                  <SizableText
                    style={{
                      color: theme ? '#374151' : '#f9fafb',
                      fontWeight: '600',
                      fontSize: 14,
                    }}
                  >
                    State/Province *
                  </SizableText>
                  <TextInput
                    value={billingData.state}
                    onChangeText={(text) =>
                      setBillingData(prev => ({ ...prev, state: text }))
                    }
                    placeholder="State"
                    placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                    style={{
                      fontSize: 16,
                      padding: 12,
                      borderWidth: 1,
                      borderColor: errors.state
                        ? '#ef4444'
                        : theme ? '#e5e7eb' : '#4b5563',
                      borderRadius: 12,
                      backgroundColor: theme ? '#f8fafc' : '#374151',
                      color: theme ? '#1f2937' : '#f9fafb',
                      minHeight: 48,
                    }}
                  />
                  {errors.state && (
                    <Text style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>
                      {errors.state}
                    </Text>
                  )}
                </YStack>
              </XStack>

              <XStack gap="$3">
                <YStack gap="$2" flex={1}>
                  <SizableText
                    style={{
                      color: theme ? '#374151' : '#f9fafb',
                      fontWeight: '600',
                      fontSize: 14,
                    }}
                  >
                    Postal Code *
                  </SizableText>
                  <TextInput
                    value={billingData.postalCode}
                    onChangeText={(text) =>
                      setBillingData(prev => ({ ...prev, postalCode: text }))
                    }
                    placeholder="12345"
                    placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                    style={{
                      fontSize: 16,
                      padding: 12,
                      borderWidth: 1,
                      borderColor: errors.postalCode
                        ? '#ef4444'
                        : theme ? '#e5e7eb' : '#4b5563',
                      borderRadius: 12,
                      backgroundColor: theme ? '#f8fafc' : '#374151',
                      color: theme ? '#1f2937' : '#f9fafb',
                      minHeight: 48,
                    }}
                  />
                  {errors.postalCode && (
                    <Text style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>
                      {errors.postalCode}
                    </Text>
                  )}
                </YStack>

                <YStack gap="$2" flex={1}>
                  <SizableText
                    style={{
                      color: theme ? '#374151' : '#f9fafb',
                      fontWeight: '600',
                      fontSize: 14,
                    }}
                  >
                    Country *
                  </SizableText>
                  <TextInput
                    value={billingData.country}
                    onChangeText={(text) =>
                      setBillingData(prev => ({ ...prev, country: text }))
                    }
                    placeholder="United States"
                    placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                    style={{
                      fontSize: 16,
                      padding: 12,
                      borderWidth: 1,
                      borderColor: errors.country
                        ? '#ef4444'
                        : theme ? '#e5e7eb' : '#4b5563',
                      borderRadius: 12,
                      backgroundColor: theme ? '#f8fafc' : '#374151',
                      color: theme ? '#1f2937' : '#f9fafb',
                      minHeight: 48,
                    }}
                  />
                  {errors.country && (
                    <Text style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>
                      {errors.country}
                    </Text>
                  )}
                </YStack>
              </XStack>

              {/* Tax Information */}
              <YStack gap="$2">
                <SizableText
                  style={{
                    color: theme ? '#374151' : '#f9fafb',
                    fontWeight: '600',
                    fontSize: 14,
                  }}
                >
                  Tax ID / VAT Number
                </SizableText>
                <TextInput
                  value={billingData.taxId}
                  onChangeText={(text) =>
                    setBillingData(prev => ({ ...prev, taxId: text }))
                  }
                  placeholder="Optional - for tax reporting"
                  placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                  style={{
                    fontSize: 16,
                    padding: 12,
                    borderWidth: 1,
                    borderColor: theme ? '#e5e7eb' : '#4b5563',
                    borderRadius: 12,
                    backgroundColor: theme ? '#f8fafc' : '#374151',
                    color: theme ? '#1f2937' : '#f9fafb',
                    minHeight: 48,
                  }}
                />
              </YStack>

              <YStack gap="$2">
                <SizableText
                  style={{
                    color: theme ? '#374151' : '#f9fafb',
                    fontWeight: '600',
                    fontSize: 14,
                  }}
                >
                  Business Registration Number
                </SizableText>
                <TextInput
                  value={billingData.businessRegistrationNumber}
                  onChangeText={(text) =>
                    setBillingData(prev => ({ ...prev, businessRegistrationNumber: text }))
                  }
                  placeholder="Optional - business registration ID"
                  placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                  style={{
                    fontSize: 16,
                    padding: 12,
                    borderWidth: 1,
                    borderColor: theme ? '#e5e7eb' : '#4b5563',
                    borderRadius: 12,
                    backgroundColor: theme ? '#f8fafc' : '#374151',
                    color: theme ? '#1f2937' : '#f9fafb',
                    minHeight: 48,
                  }}
                />
              </YStack>

              {/* Payment Method Note */}
              <View
                style={{
                  backgroundColor: theme ? '#fef3c7' : '#451a03',
                  borderRadius: 12,
                  padding: 16,
                  borderWidth: 1,
                  borderColor: theme ? '#fde68a' : '#78350f',
                }}
              >
                <XStack alignItems="center" gap="$2">
                  <MaterialIcons
                    name="info"
                    size={20}
                    color={theme ? "#f59e0b" : "#fbbf24"}
                  />
                  <Text style={{
                    color: theme ? '#92400e' : '#fbbf24',
                    fontSize: 14,
                    flex: 1,
                  }}>
                    Payment method will be collected during the subscription setup process after company approval.
                  </Text>
                </XStack>
              </View>
            </>
          )}
        </YStack>
      </ScrollView>
    );
  };

  const renderStep3 = () => (
    <ScrollView style={{ maxHeight: 500 }}>
      <YStack gap="$4">
        {/* Company Information Review */}
        <View
          style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            borderRadius: 16,
            padding: 20,
            borderWidth: 1,
            borderColor: theme ? '#e5e7eb' : '#4b5563',
          }}
        >
          <XStack alignItems="center" gap="$2" marginBottom="$3">
            <MaterialIcons
              name="business"
              size={20}
              color={theme ? "#3b82f6" : "#60a5fa"}
            />
            <Text style={{
              fontSize: 18,
              fontWeight: '700',
              color: theme ? '#1e293b' : '#f1f5f9',
            }}>
              Company Information
            </Text>
            <Pressable
              onPress={() => setCurrentStep(1)}
              style={{ marginLeft: 'auto' }}
            >
              <Text style={{
                color: theme ? '#3b82f6' : '#60a5fa',
                fontSize: 14,
                fontWeight: '600',
              }}>
                Edit
              </Text>
            </Pressable>
          </XStack>
          <YStack gap="$2">
            <XStack justifyContent="space-between">
              <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14 }}>
                Company Name:
              </Text>
              <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 14, fontWeight: '600' }}>
                {companyData.company_name || 'Not provided'}
              </Text>
            </XStack>
            {companyData.company_number && (
              <XStack justifyContent="space-between">
                <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14 }}>
                  Company Number:
                </Text>
                <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 14, fontWeight: '600' }}>
                  {companyData.company_number}
                </Text>
              </XStack>
            )}
            {companyData.industry_type && (
              <XStack justifyContent="space-between">
                <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14 }}>
                  Industry:
                </Text>
                <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 14, fontWeight: '600' }}>
                  {companyData.industry_type}
                </Text>
              </XStack>
            )}
            {companyData.country && (
              <XStack justifyContent="space-between">
                <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14 }}>
                  Country:
                </Text>
                <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 14, fontWeight: '600' }}>
                  {companyData.country}
                </Text>
              </XStack>
            )}
          </YStack>
        </View>

        {/* Billing Information Review */}
        <View
          style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            borderRadius: 16,
            padding: 20,
            borderWidth: 1,
            borderColor: theme ? '#e5e7eb' : '#4b5563',
          }}
        >
          <XStack alignItems="center" gap="$2" marginBottom="$3">
            <MaterialIcons
              name="payment"
              size={20}
              color={theme ? "#10b981" : "#34d399"}
            />
            <Text style={{
              fontSize: 18,
              fontWeight: '700',
              color: theme ? '#1e293b' : '#f1f5f9',
            }}>
              Billing Information
            </Text>
            <Pressable
              onPress={() => setCurrentStep(2)}
              style={{ marginLeft: 'auto' }}
            >
              <Text style={{
                color: theme ? '#3b82f6' : '#60a5fa',
                fontSize: 14,
                fontWeight: '600',
              }}>
                Edit
              </Text>
            </Pressable>
          </XStack>
          <YStack gap="$2">
            {billingData.useExistingBilling ? (
              <Text style={{ color: theme ? '#059669' : '#10b981', fontSize: 14, fontWeight: '600' }}>
                Using account email (new billing account will be created)
              </Text>
            ) : (
              <>
                <XStack justifyContent="space-between">
                  <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14 }}>
                    Billing Email:
                  </Text>
                  <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 14, fontWeight: '600' }}>
                    {billingData.billingEmail || 'Not provided'}
                  </Text>
                </XStack>
                {billingData.billingPhone && (
                  <XStack justifyContent="space-between">
                    <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14 }}>
                      Phone:
                    </Text>
                    <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 14, fontWeight: '600' }}>
                      {billingData.billingPhone}
                    </Text>
                  </XStack>
                )}
                {billingData.streetAddress1 && (
                  <>
                    <XStack justifyContent="space-between">
                      <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14 }}>
                        Address:
                      </Text>
                      <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 14, fontWeight: '600', flex: 1, textAlign: 'right' }}>
                        {billingData.streetAddress1}
                        {billingData.streetAddress2 && `, ${billingData.streetAddress2}`}
                      </Text>
                    </XStack>
                    <XStack justifyContent="space-between">
                      <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14 }}>
                        City, State, Postal:
                      </Text>
                      <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 14, fontWeight: '600' }}>
                        {billingData.city}, {billingData.state} {billingData.postalCode}
                      </Text>
                    </XStack>
                    <XStack justifyContent="space-between">
                      <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14 }}>
                        Country:
                      </Text>
                      <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 14, fontWeight: '600' }}>
                        {billingData.country}
                      </Text>
                    </XStack>
                  </>
                )}
                {billingData.taxId && (
                  <XStack justifyContent="space-between">
                    <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14 }}>
                      Tax ID:
                    </Text>
                    <Text style={{ color: theme ? '#1e293b' : '#f1f5f9', fontSize: 14, fontWeight: '600' }}>
                      {billingData.taxId}
                    </Text>
                  </XStack>
                )}
              </>
            )}
          </YStack>
        </View>
      </YStack>
    </ScrollView>
  );

  return (
    <Dialog open={addCompanyVisible} modal>
      <Dialog.Portal>
        <Dialog.Overlay
          animation="quick"
          opacity={0.6}
          style={{
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(8px)',
            })
          }}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
        <Dialog.Content
          bordered
          elevate
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          x={0}
          scale={1}
          style={[
            {
              padding: 0,
              minWidth: 600,
              maxWidth: 800,
              maxHeight: "90%",
              borderRadius: 24,
              overflow: 'hidden',
              elevation: 12,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 12 },
              shadowOpacity: 0.15,
              shadowRadius: 32,
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
              borderWidth: 1,
              borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
            },
            Platform.OS === 'web' && {
              backdropFilter: 'blur(24px)',
              boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
            }
          ]}
          opacity={1}
          y={0}
        >
          {/* Header */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 20,
            borderBottomWidth: 1,
            borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack alignItems="center" gap="$3">
              <View style={{
                backgroundColor: theme ? "#f0f9ff" : "#0c4a6e",
                borderRadius: 12,
                padding: 10,
              }}>
                <MaterialIcons
                  name="business"
                  size={20}
                  color={theme ? "#0ea5e9" : "#38bdf8"}
                />
              </View>
              <YStack flex={1}>
                <Dialog.Title style={{
                  fontSize: 20,
                  fontWeight: '700',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  marginBottom: 4,
                  margin: 0,
                }}>
                  Add a Company
                </Dialog.Title>
                <Dialog.Description style={{
                  fontSize: 14,
                  color: theme ? '#64748b' : '#94a3b8',
                  fontWeight: '500',
                  margin: 0,
                }}>
                  Adding a company or organisation to your account is easy with SmartSaaS, just bear in mind that additional companies are billed separately. Companies require approval by admin.
                </Dialog.Description>
              </YStack>
              <Pressable onPress={handleClose}>
                <AntDesign
                  name="close"
                  size={24}
                  color={theme ? '#6b7280' : '#9ca3af'}
                />
              </Pressable>
            </XStack>
          </View>

          {/* Step Indicator */}
          {renderStepIndicator()}

          {/* Content */}
          <View style={{ padding: 24, minHeight: 400 }}>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </View>

          {/* Footer Actions */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 16,
            borderTopWidth: 1,
            borderTopColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack gap="$3" justifyContent="flex-end">
              {currentStep > 1 && (
                <Pressable
                  onPress={handleBack}
                  disabled={loading}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: theme ? '#f1f5f9' : '#4b5563',
                    borderWidth: 1,
                    borderColor: theme ? '#e2e8f0' : '#6b7280',
                    minWidth: 100,
                    alignItems: 'center',
                    opacity: loading ? 0.5 : 1,
                  }}
                >
                  <Text style={{
                    color: theme ? '#64748b' : '#d1d5db',
                    fontWeight: '600',
                    fontSize: 14,
                  }}>
                    Back
                  </Text>
                </Pressable>
              )}
              {currentStep < 3 ? (
                <Pressable
                  onPress={handleNext}
                  disabled={loading}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: theme ? '#3b82f6' : '#2563eb',
                    minWidth: 100,
                    alignItems: 'center',
                    elevation: 2,
                    shadowColor: '#3b82f6',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.2,
                    shadowRadius: 8,
                    opacity: loading ? 0.5 : 1,
                  }}
                >
                  <Text style={{
                    color: '#ffffff',
                    fontWeight: '600',
                    fontSize: 14,
                  }}>
                    Next
                  </Text>
                </Pressable>
              ) : (
                <Pressable
                  onPress={handleSubmit}
                  disabled={loading}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: theme ? '#10b981' : '#059669',
                    minWidth: 120,
                    alignItems: 'center',
                    elevation: 2,
                    shadowColor: '#10b981',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.2,
                    shadowRadius: 8,
                    opacity: loading ? 0.5 : 1,
                    flexDirection: 'row',
                    gap: 8,
                  }}
                >
                  {loading ? (
                    <>
                      <ActivityIndicator size="small" color="#ffffff" />
                      <Text style={{
                        color: '#ffffff',
                        fontWeight: '600',
                        fontSize: 14,
                      }}>
                        Submitting...
                      </Text>
                    </>
                  ) : (
                    <Text style={{
                      color: '#ffffff',
                      fontWeight: '600',
                      fontSize: 14,
                    }}>
                      Submit Company
                    </Text>
                  )}
                </Pressable>
              )}
            </XStack>
          </View>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

export default memo(AddCompanyModal);
